import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';

interface Props {
  onClose: () => void;
}

interface CreateProjectPayload {
  name: string;
  expertise_type?: 'gov' | 'private' | 'internal';
}

export function CreateProjectModal({ onClose }: Props) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [client, setClient] = useState('');
  const [designer, setDesigner] = useState('');
  const [expertiseType, setExpertiseType] = useState<'gov' | 'private' | 'internal'>('gov');

  const create = useMutation({
    mutationFn: (p: CreateProjectPayload) => api.post<{ id: string }>('/projects', p),
    onSuccess: (data) => {
      toast.success(`Проект создан: ${data.id.slice(0, 8)}`);
      qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось создать проект'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !title.trim()) return;
    // Backend хранит name как одну строку. Композим: "{code} — {title}"
    const name = `${code.trim()} — ${title.trim()}`;
    create.mutate({ name, expertise_type: expertiseType });
  };

  return (
    <Modal
      title="Создать проект"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" onClick={(e) => submit(e as unknown as FormEvent)} disabled={!code.trim() || !title.trim() || create.isPending}>
            {create.isPending ? 'Создание…' : 'Создать'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col" style={{ gap: 16 }}>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Шифр проекта</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="УХТ-2401"
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14, fontFamily: 'monospace' }}
          />
          <p className="text-muted mt-1" style={{ fontSize: 11 }}>Используется в карточках и заголовках, например УХТ-2401</p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Полное название</label>
          <textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            rows={2}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14, fontFamily: 'Inter, system-ui, sans-serif' }}
            placeholder="Реконструкция железнодорожного пути №51 на участке…"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Заказчик (опц.)</label>
          <input
            type="text"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14 }}
            placeholder="ООО «РЖДпроект»"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Проектировщик (опц.)</label>
          <input
            type="text"
            value={designer}
            onChange={(e) => setDesigner(e.target.value)}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14 }}
            placeholder="АО «Сибирский проектный институт»"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">Тип экспертизы</label>
          <div className="inline-flex bg-surface rounded-pill" style={{ padding: 4, gap: 4 }}>
            {([
              { id: 'gov', label: 'Государственная' },
              { id: 'private', label: 'Негосударственная' },
              { id: 'internal', label: 'Внутренняя проектировщика' },
            ] as const).map((opt) => {
              const active = expertiseType === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setExpertiseType(opt.id)}
                  className="rounded-pill"
                  style={{
                    padding: '6px 14px',
                    background: active ? '#FFFFFF' : 'transparent',
                    boxShadow: active ? '0 1px 3px rgba(10,22,40,.08)' : 'none',
                    fontWeight: active ? 600 : 500, fontSize: 13,
                    color: active ? '#0A1628' : '#6B7280',
                    border: 'none', cursor: 'pointer',
                  }}
                >{opt.label}</button>
              );
            })}
          </div>
        </div>
      </form>
    </Modal>
  );
}
